import { eventExportMessage } from '@/services/event';
import { exportMessage } from '@/services/export_message';
import { Visibility } from '@/types';

export type DebouncedFunction<T extends (...args: any[]) => void> = T & {
    cancel: () => void;
};

export function debounce(
    fn: (...args: any[]) => void,
    delay: number
): DebouncedFunction<(...args: any[]) => void> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    function debounced(...args: any[]) {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            timeoutId = undefined;
            fn(...args);
        }, delay);
    }

    debounced.cancel = () => {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
        timeoutId = undefined;
    };

    return debounced;
}

export function truncateString(str: string, maxLength: number): string {
    return str.length > maxLength ? `${str.slice(0, maxLength)}...` : str;
}

export function formatDate(iso: string | undefined): string {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

export function formatDateTime(iso: string | undefined): string {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

// A bare ISO datetime is UTC. Without an offset it parses as local time.
export function asUtc(value: string): string {
    return /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`;
}

export function formatShortDateTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

export function formatDuration(seconds: number | null | undefined): string {
    if (seconds == null) return '—';
    return `${seconds.toFixed(1)}s`;
}

export function isSessionSelected(sessionId: string, route: string) {
    return sessionId === route;
}

export function scrollContainerToTop(container: HTMLElement | null) {
    if (!container) return;
    container?.scrollTo({
        top: 0,
        behavior: 'smooth'
    });
}
export function scrollContainerToBottom(container: HTMLElement | null) {
    if (!container) return;
    container?.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
    });
}

export const exportHTMLToDocx = async (
    htmlContent: string,
    tenantId: string,
    toast: {
        toast: (options: {
            title: string;
            description: string;
            variant: 'default' | 'destructive' | null | undefined;
            duration: number;
        }) => void;
    },
    sessionId?: string,
    messageId?: string,
    message?: string,
    // Doc studio only: the document being exported, which the backend counts as a doc export.
    documentId?: string
) => {
    if (htmlContent) {
        if (!tenantId) {
            console.error('Tenant ID is missing');
            return;
        }
        try {
            const blob = await exportMessage(tenantId, htmlContent, documentId);
            downloadBlob(blob, 'playground_exported_message.docx');
            toast.toast({
                title: 'Export Done',
                description: 'Your document has been exported successfully',
                variant: 'default',
                duration: 3000
            });
        } catch (error) {
            toast.toast({
                title: 'Export issue',
                description: 'Something failed during the export',
                variant: 'destructive',
                duration: 3000
            });
        }
    }
    if (tenantId && sessionId) {
        await eventExportMessage(
            messageId || '',
            tenantId,
            message || '',
            sessionId
        );
    }
};

export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export function openExternalLink(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
}

export const canEditOrDeleteResource = (
    resource: any,
    userId: string,
    tenantAdmin: boolean,
    tenantElqano = false
) => {
    const isAuthor = resource.author_id === userId;
    const isAdminAndPublic =
        tenantAdmin && resource.visibility === Visibility.PUBLIC;
    // Any Elqano user may curate global catalog items, not only the author.
    const isElqanoUserAndGlobal =
        tenantElqano && resource.visibility === Visibility.GLOBAL;
    return isAuthor || isAdminAndPublic || isElqanoUserAndGlobal;
};
